import React, { useCallback, useEffect, useState } from "react";
import "./InstitutionProfile.css";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Collapse,
  Spin,
  Descriptions,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  AutoComplete,
  DatePicker,
  Select,
  Switch,
  Radio,
  message,
  Typography,
  Upload,
  Tooltip,
  Space,
  Divider,
  Alert,
  Tag,
} from "antd";
import { EditOutlined, InfoCircleOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { ReactComponent as MagicWand } from "../../../assets/icons/magic-wand.svg";
import dayjs from "dayjs";
import { fetchInstitution, updateInstitution } from "./institutionThunk";
import PhoneInput from "react-phone-input-2";
import OnboardingWizard from "../../../components/onboarding/OnboardingWizard";
import { formatBulletText, getAccessType } from "../../../lib/functions";
import { getISOFromCountryName } from "../../../lib/currency";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { useIsAdminUser } from "../../../lib/accessList";
import { useLocation, useNavigate } from "react-router-dom";
import TimeZoneSelect from "../../../components/TimeZoneSelect/TimeZoneSelect";

const { Panel } = Collapse;
const { Option } = Select;
const { Text } = Typography;

const WEEK_DAYS = {
  0: "Sunday",
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday",
};

const formatWeekendDays = (days) => {
  if (!days) return "";
  const arr = Array.isArray(days) ? days : String(days).split(",");
  return arr.map((d) => WEEK_DAYS[d] || d).join(", ");
};

// Image Validator
const validateImage = ({
  file,
  maxWidth,
  maxHeight,
  ratio,
  maxSizeMB = 2,
}) => {
  return new Promise((resolve, reject) => {
    // Type check
    if (!["image/png", "image/jpeg", "image/jpg"].includes(file.type)) {
      return reject("Only PNG or JPG images are allowed");
    }

    // Size check
    if (file.size / 1024 / 1024 > maxSizeMB) {
      return reject(`Image must be less than ${maxSizeMB}MB`);
    }

    const img = new Image();
    const reader = new FileReader();

    reader.onload = () => {
      img.src = reader.result;
    };

    img.onload = () => {
      const { width, height } = img;

      if (width > maxWidth || height > maxHeight) {
        return reject(
          `Image size must be within ${maxWidth}×${maxHeight}px`
        );
      }

      // if (ratio) {
      //   const actualRatio = width / height;
      //   const tolerance = 0.05;

      //   if (Math.abs(actualRatio - ratio) > tolerance) {
      //     return reject(`Image aspect ratio is invalid`);
      //   }
      // }

      resolve(true);
    };

    reader.onerror = reject;
    img.onerror = reject;

    reader.readAsDataURL(file);
  });
};

const validateLogo = async (file) =>
  validateImage({
    file,
    maxWidth: 600,
    maxHeight: 600,
    ratio: 1,
  });

const validateStamp = async (file) =>
  validateImage({
    file,
    maxWidth: 600,
    maxHeight: 600,
    ratio: 1,
  });

const validateSignature = async (file) =>
  validateImage({
    file,
    maxWidth: 800,
    maxHeight: 400,
    ratio: 2,
  });



// handle image preview
const handlePreview = async (file) => {
  let src = file.url || file.preview;

  if (!src && file.originFileObj) {
    src = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file.originFileObj);
      reader.onload = () => resolve(reader.result);
    });
  }

  const image = new Image();
  image.src = src;

  const imgWindow = window.open(src);
  imgWindow?.document.write(image.outerHTML);
};


const InstitutionProfile = () => {
  const dispatch = useDispatch();

  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const isOnboarding = params.get("onboarding") === "true";

  const { data, loading, error } = useSelector((state) => state.institution);
  console.log('dataL::::', data)
  const authInfo = useSelector((state) => state.auth?.auth_info);
  const isIndia = authInfo?.country?.toLowerCase() === "india";
  const isUAE = authInfo?.country?.toLowerCase() === "united arab emirates";
  const taxLabel = isIndia ? "GSTIN" : "VAT Registration No";
  const gstEnabled = authInfo?.gst_enabled === "yes";
  const showTax = !isIndia || gstEnabled;
  const billType = isIndia ? "GST & Discount on Bill Type" : "Vat & Discount on Bill Type";


  const [saving, setSaving] = useState(false);
  const [openModal, setOpenModal] = useState(null);
  const [form] = Form.useForm();

  const [logoFileList, setLogoFileList] = useState([]);
  const [stampFileList, setStampFileList] = useState([]);
  const [signatureFileList, setSignatureFileList] = useState([]);
  let [access_type, access_type_set] = useState("");
  const [showWizard, setShowWizard] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [currentStepKey, setCurrentStepKey] = useState(null);

  // const hasInitialized = React.useRef(false); //onboarding wizard initialized check

  const defaultCountry = getISOFromCountryName(authInfo?.country);

  const isMobile = window.innerWidth < 768;
  const isAdminUser = useIsAdminUser();
  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;

  const toUploadFileList = (url, fieldName) => {
    if (!url) return [];
    return [
      {
        uid: `-${fieldName}`,
        name: url.split("/").pop(),
        status: "done",
        url,
      },
    ];
  };

  useEffect(() => {
    const access = getAccessType("employee_entry");
    access_type_set(access);
  }, []);

  useEffect(() => {
    dispatch(fetchInstitution());
  }, [dispatch]);

  useEffect(() => {
    if (data) {
      setLogoFileList(toUploadFileList(data.branchLogo, "logo"));
      setStampFileList(toUploadFileList(data.branchStamp, "stamp"));
      setSignatureFileList(toUploadFileList(data.branchSignature, "signature"));
    }
  }, [data]);

  // useEffect(() => {
  //   if (isOnboarding && !showWizard) {
  //     setShowWizard(true);
  //   }
  // }, [isOnboarding]);

  useEffect(() => {
    if (isOnboarding) {
      setShowWizard(true);
    } else {
      setShowWizard(false);
    }
  }, [isOnboarding]);

  // useEffect(() => {
  //   if (isOnboarding && !hasInitialized.current) {
  //     setShowWizard(true);
  //     hasInitialized.current = true;
  //   }
  // }, [isOnboarding]);

  // useEffect(() => {
  //   if (!isOnboarding) {
  //     hasInitialized.current = false;
  //   }
  // }, [isOnboarding]); 

  useEffect(() => {
    if (!showWizard) {
      setOnboardingStep(0);
    }
  }, [showWizard]);

  const removeOnboardingParam = useCallback(() => {
    const params = new URLSearchParams(location.search);

    params.delete("onboarding");
    params.delete("manual");

    navigate(
      {
        pathname: location.pathname,
        search: params.toString(),
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  const isManualMode = params.get("manual") === "true";

  useEffect(() => {
    if (
      data?.onboarding_status === "completed" &&
      !isManualMode
    ) {
      setShowWizard(false);
      removeOnboardingParam();
    }
  }, [
    data?.onboarding_status,
    isManualMode,
    removeOnboardingParam,
  ]);


  // ✅ include isCalType now
  const toFormValues = (raw) => {
    if (!raw) return {};

    // Combine country code and phone number so PhoneInput can parse it properly
    const fullPhone = raw.countryCode && raw.phoneNo
      ? `${raw.countryCode}${raw.phoneNo}`
      : (raw.phoneNo || "");

    return {
      companyName: raw.companyName || "",
      legalName: raw.legalName || "",
      einSsn: raw.einSsn || "",
      invoiceStartNumber: raw.invoiceStartNumber || 1,
      taxForm: raw.taxForm || "",
      subscriptionType: raw.subscriptionType || "",
      subscriptionExpiry: raw.subscriptionExpiry
        ? dayjs(raw.subscriptionExpiry, "DD/MM/YYYY")
        : null,
      companyType: raw.companyType || "",
      companyEmail: raw.companyEmail || "",
      phoneNo: fullPhone,
      webSite: raw.webSite || "",
      timeZone: raw.timeZone || "",
      streetAddress: raw.streetAddress || "",
      thermalPrint: raw.thermalPrint || raw.thermal_print === 'yes' || false,
      country: raw.country || "",
      countryCode: raw.countryCode || "",
      poBox: raw.poBox || "",
      currency: raw.currency || "",
      vatPercent: raw.vatPercent || 0,
      vatLicense: raw.vatLicense || "",
      tradeLicenseNo: raw.tradeLicenseNo || "",
      tradeLicenseExpiry: raw.tradeLicenseExpiry
        ? dayjs(raw.tradeLicenseExpiry, "DD/MM/YYYY")
        : null,
      dhaMohNo: raw.dhaMohNo || "",
      dhaMohExpiry: raw.dhaMohExpiry
        ? dayjs(raw.dhaMohExpiry, "DD/MM/YYYY")
        : null,
      glnNo: raw.glnNo || "",
      glnExpiry: raw.glnExpiry ? dayjs(raw.glnExpiry, "DD/MM/YYYY") : null,
      description: raw.description || "",
      annualLeaveType: raw.annualLeaveType || 1,
      annualLeaveDays: raw.annualLeaveDays || 0,
      sickLeaveDays: raw.sickLeaveDays || 0,
      flexiLeaveDays: raw.flexiLeaveDays || 0,
      enableLateDeduction: raw.enableLateDeduction || false,
      lateDeductionType: raw.lateDeductionType || "half_day",
      lateDeductionAfterMinutes: raw.lateDeductionAfterMinutes || 30,
      lateDeductionLeaveType: raw.lateDeductionLeaveType || "casual_leave",
      enableUnmarkedAttendanceDeduction: raw.enableUnmarkedAttendanceDeduction || false,
      unmarkedAttendanceDeductionType: raw.unmarkedAttendanceDeductionType || "casual_leave",
      unmarkedAttendanceToleranceDays: raw.unmarkedAttendanceToleranceDays ?? 0,
      leaveBackdateDays: raw.leaveBackdateDays ?? 7,
      sickLeaveBackdateDays: raw.sickLeaveBackdateDays ?? 7,
      weekendDays: raw.weekendDays ? String(raw.weekendDays).split(",").map((d) => Number(d)) : [],
      shouldResetAccruedLeave: raw.shouldResetAccruedLeave || false,
      leavesCarryForward: raw.leavesCarryForward || 0,
      autoCheckoutDurationMinutes: raw.autoCheckoutDurationMinutes || "",
      branchReturnPolicy: raw.branchReturnPolicy || 0,
      branchReplacePolicy: raw.branchReplacePolicy || 0,
      invoicePrintType: raw.invoicePrintType || "a4",
      isWarehouse: raw.isWarehouse || false,
      fyStartMonth: raw.fy_start_month || 1,
      isAutoChallan: raw.isAutoChallan || false,
      isVoucherReceipt: raw.isVoucherReceipt || false,
      isMinusStockSale: raw.isMinusStockSale || false,
      allowProductRateChange: raw.allowProductRateChange || false,
      enableCrossBranchItemSync: raw.enableCrossBranchItemSync || false,
      isSerial: raw.isSerial || false,
      enableBatchNumber: raw.enableBatchNumber ?? true,
      applyDiscountToAvgPrice: raw.applyDiscountToAvgPrice || false,
      isCalType: raw.isCalType || "on_total", // ✅ ensure default
      customerCodePrefix: raw.customerCodePrefix || "CMR",
      quotationTerms: raw.quotationTerms || "",
      quotationPaymentTerms: raw.quotationPaymentTerms || "",
      purchaseOrderPaymentTerms: raw.purchaseOrderPaymentTerms || "",
      migrationDate: raw.migrationDate
        ? dayjs(raw.migrationDate).isValid()
          ? dayjs(raw.migrationDate)
          : null
        : null,
      vatStaggerMonth: raw.vat_stagger_month || 1,
      vatDeadlineDay: raw.vat_deadline_day || null,

      // Design A print layout -- see backend/src/pdfTemplates/designA/. Bank
      // details for it come from Chart of Accounts (the bank account marked
      // primary there), not from a field in this form.
      printTemplate: raw.printTemplate || "design_a",
      invoiceDefaultTerms: raw.invoiceDefaultTerms || "",
    };
  };

  const handleEdit = (section) => {
    if (!data) return;
    form.setFieldsValue(toFormValues(data));
    setOpenModal(section);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      // 1. Prepare base merged values safely without wiping out hidden modal values
      const mergedValues = {
        ...data,
        ...values,
        // Only override weekendDays if it's on the active form screen
        weekendDays: Object.hasOwn(values, "weekendDays")
          ? (values.weekendDays || []).map(Number)
          : (data.weekendDays ? String(data.weekendDays).split(",").map(Number) : []),

        // Only override Late Deduction rules if they are on the active form screen
        enableLateDeduction: Object.hasOwn(values, "enableLateDeduction")
          ? values.enableLateDeduction
          : data.enableLateDeduction,

        lateDeductionType: Object.hasOwn(values, "enableLateDeduction")
          ? (!values.enableLateDeduction ? null : values.lateDeductionType)
          : data.lateDeductionType,

        lateDeductionAfterMinutes: Object.hasOwn(values, "enableLateDeduction")
          ? (!values.enableLateDeduction ? null : values.lateDeductionAfterMinutes)
          : data.lateDeductionAfterMinutes,

        branchLogo: logoFileList,
        branchStamp: stampFileList,
        branchSignature: signatureFileList,
        fy_start_month: values.fyStartMonth || data.fy_start_month || data.fyStartMonth,
        migration_date: values.migrationDate
          ? values.migrationDate.format("YYYY-MM-DD")
          : data.migrationDate
            ? dayjs(data.migrationDate).format("YYYY-MM-DD")
            : null,
      };

      // 2. Map phone details using camelCase so buildInstitutionFormData reads them properly
      if (openModal === "info") {
        if (values.phoneNo) {
          const normalized = values.phoneNo.startsWith("+") ? values.phoneNo : `+${values.phoneNo}`;
          const parsed = parsePhoneNumberFromString(normalized);
          if (parsed) {
            mergedValues.countryCode = parsed.countryCallingCode; // e.g. "971"
            mergedValues.phoneNo = parsed.nationalNumber;         // e.g. "564883091"
          } else {
            mergedValues.countryCode = data.countryCode || "971";
            mergedValues.phoneNo = values.phoneNo;
          }
        } else {
          mergedValues.countryCode = "";
          mergedValues.phoneNo = "";
        }
      } else {
        // Retain pristine existing entries for other modal operations
        mergedValues.countryCode = data.countryCode || "971";
        mergedValues.phoneNo = data.phoneNo || "";
      }

      // Clean up proxy UI/Date fields to keep payload clean
      delete mergedValues.migrationDate;

      setSaving(true);
      const res = await dispatch(
        updateInstitution({ values: mergedValues, authInfo })
      );
      if (updateInstitution.fulfilled.match(res)) {
        message.success("Institution updated successfully");
        setOpenModal(null);
        window.dispatchEvent(new Event("onboarding-save"));
        setTimeout(() => {
          dispatch(fetchInstitution());
        }, 300);
      } else {
        if (typeof res.payload === "string") {
          message.error(res.payload);
        } else {
          message.error("Failed to update institution. Please check inputs.");
        }
      }
    } catch (err) {
      console.error(err);
      message.error(err?.data?.message || "Validation failed. Please check your inputs.");
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <Spin size="large" style={{ display: "block", margin: "2rem auto" }} />
    );
  if (error)
    return (
      <Card>
        <p style={{ color: "red" }}>{error}</p>
      </Card>
    );
  if (!data)
    return (
      <Card>
        <p>No institution data</p>
      </Card>
    );
  console.log('data', data);
  const panelHeader = (title, onEdit, key) => {
    const isActiveStep = key && currentStepKey === key;
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Text strong style={{ fontSize: "16px" }}>
          {title}
        </Text>
        {access_type == "WRITE" && (
          <>
            {onEdit && (
              <Button
                className={isActiveStep ? "pulse-btn" : ""}
                type="link"
                data-testid="btn-edit"
                disabled={!(isAdminUser || canEdit)}
                icon={<EditOutlined />}
                onClick={(e) => {
                  e.stopPropagation();   // ⭐ KEY LINE
                  onEdit();
                }}
              />
            )}
          </>
        )}
      </div>
    )
  };

  const descProps = {
    column: 1,
    bordered: true,
    styles: {
      label: {
        width: isMobile ? "40%" : "250px",
        whiteSpace: "normal",
      },
      content: {
        width: isMobile ? "60%" : "calc(100% - 250px)",
        wordBreak: "break-word",
      },
    },
  };

  return (
    <Card
      data-testid="card-company-profile"
      // title="Company Profile"
      style={{ maxWidth: "1000px", marginInline: "auto" }}
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>Company Profile</span>

          {isAdminUser && (
            <Tooltip title="Start Onboarding Guide">
              <Button
                type="text"
                icon={<MagicWand />}
                onClick={() =>
                  navigate(
                    "/settings/institution-profile?onboarding=true&manual=true",
                  )
                }
              />
            </Tooltip>
          )}
        </div>
      }
    >
      <Collapse
        bordered={false}
        defaultActiveKey={[
          "company",
          "companyType",
          "info",
          "address",
          "finance",
          "hr",
          "bill",
          "count",
        ]}
      >
        {/* Panels */}
        <Panel
          key="company"
          header={panelHeader("Company", () => handleEdit("company"))}
          data-onboard="company"
        >
          <Descriptions {...descProps}>
            <Descriptions.Item label="Company Logo">
              {data.branchLogo ? (
                <img
                  src={data.branchLogo}
                  alt="Company Logo"
                  style={{
                    maxHeight: "60px",
                    maxWidth: "120px",
                    objectFit: "contain",
                  }}
                />
              ) : (
                "N/A"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Company Name">
              {data.companyName}
            </Descriptions.Item>
            <Descriptions.Item label="Legal Name">
              {data.legalName}
            </Descriptions.Item>
            <Descriptions.Item label="EIN/SSN">{data.einSsn}</Descriptions.Item>
            <Descriptions.Item label="Invoice Start Number">
              {data.invoiceStartNumber}
            </Descriptions.Item>
          </Descriptions>
        </Panel>

        <Panel
          key="companyType"
          header={panelHeader("Company Type", () => handleEdit("companyType"))}
          data-onboard="companyType"
        >
          <Descriptions {...descProps}>
            <Descriptions.Item label="Tax Form">
              {data.taxForm}
            </Descriptions.Item>
            <Descriptions.Item label="Subscription Type">
              {data.subscriptionType}
            </Descriptions.Item>
            <Descriptions.Item label="Subscription Expiry">
              {data.subscriptionExpiry}
            </Descriptions.Item>
            <Descriptions.Item label="Company Type">
              {data.companyType}
            </Descriptions.Item>
          </Descriptions>
        </Panel>

        <Panel
          key="info"
          header={panelHeader("Company Info", () => handleEdit("info"))}
          data-onboard="info"
        >
          <Descriptions {...descProps}>
            <Descriptions.Item label="Email">
              {data.companyEmail}
            </Descriptions.Item>
            <Descriptions.Item label="Phone">+{data.countryCode}{data.phoneNo}</Descriptions.Item>
            <Descriptions.Item label="Website">
              {data.webSite}
            </Descriptions.Item>
            <Descriptions.Item label="Time Zone">
              {data.timeZone}
            </Descriptions.Item>
          </Descriptions>
        </Panel>

        <Panel
          key="address"
          header={panelHeader("Address", () => handleEdit("address"))}
          data-onboard="address"
        >
          <Descriptions {...descProps}>
            <Descriptions.Item label="Street Address">
              {data.streetAddress}
            </Descriptions.Item>
            <Descriptions.Item label="Country">
              {data.country}
            </Descriptions.Item>
            <Descriptions.Item label="PO Box">{data.poBox}</Descriptions.Item>
          </Descriptions>
        </Panel>

        <Panel
          key="finance"
          header={panelHeader("Finance & Compliance", () =>
            handleEdit("finance"),
          )}
          data-onboard="finance"
        >
          <Descriptions {...descProps}>
            <Descriptions.Item label="Customer Code Prefix">
              {data.customerCodePrefix}
            </Descriptions.Item>
            <Descriptions.Item label="Currency">
              {data.currency}
            </Descriptions.Item>
            {/* <Descriptions.Item label="VAT %">
              {data.vatPercent}
            </Descriptions.Item> */}
            {showTax && (
              <Descriptions.Item label={taxLabel}>
                {data.vatLicense}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Trade License">
              {data.tradeLicenseNo}
            </Descriptions.Item>
            <Descriptions.Item label="Trade License Expiry">
              {typeof data.tradeLicenseExpiry === "string"
                ? data.tradeLicenseExpiry
                : null}
            </Descriptions.Item>
            {["Drug Store", "Pharmacy"].includes(authInfo?.company_type) ||
              authInfo?.subscription === "SuperERP" ? (
              <>
                <Descriptions.Item label="DHA/MOH No">
                  {data.dhaMohNo}
                </Descriptions.Item>
                <Descriptions.Item label="DHA/MOH Expiry">
                  {data.dhaMohExpiry}
                </Descriptions.Item>
                <Descriptions.Item label="GLN No">
                  {data.glnNo}
                </Descriptions.Item>
                <Descriptions.Item label="GLN Expiry">
                  {data.glnExpiry}
                </Descriptions.Item>
              </>
            ) : null}
            <Descriptions.Item label="Description">
              {data.description}
            </Descriptions.Item>
            <Descriptions.Item label="Financial Year Start">
              {data.fy_start_month
                ? dayjs().month(data.fy_start_month - 1).format('MMMM')
                : 'January'}
            </Descriptions.Item>
            <Descriptions.Item label="Migration Date">
              {data.migrationDate
                ? dayjs(data.migrationDate).format("DD/MM/YYYY")
                : "N/A"}
            </Descriptions.Item>
            {!isIndia && (
              <Descriptions.Item label="VAT Stagger Month">
                {data.vat_stagger_month
                  ? dayjs().month(data.vat_stagger_month - 1).format("MMMM")
                  : "N/A"}
              </Descriptions.Item>
            )}

            {showTax && (
              <Descriptions.Item label={isIndia ? "GST Filing Deadline" : "VAT Filing Deadline"}>
                {data.vat_deadline_day
                  ? data.vat_deadline_day
                  : "N/A"}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Print Layout">
              {data.printTemplate === "legacy" ? "Legacy (previous layout)" : "Design A (new)"}
            </Descriptions.Item>
            <Descriptions.Item label="Bank Details">
              Shown on Design A documents from the bank account marked primary in{" "}
              <Typography.Link onClick={() => navigate("/accounts/account-entry")}>
                Chart of Accounts
              </Typography.Link>
            </Descriptions.Item>
          </Descriptions>

        </Panel>

        <Panel
          key="hr"
          header={panelHeader("HR Module", () => handleEdit("hr"))}
          data-onboard="hr"
        >
          <Descriptions {...descProps}>
            <Descriptions.Item label={isIndia ? "Casual Leave Type" : "Annual Leave Type"}>
              {data.annualLeaveType}
            </Descriptions.Item>
            <Descriptions.Item label={isIndia ? "Casual Leave Days" : "Annual Leave Days"}>
              {data.annualLeaveDays}
            </Descriptions.Item>
            <Descriptions.Item label="Max Sick Leave Days">
              {data.sickLeaveDays}
            </Descriptions.Item>
            <Descriptions.Item label="Max Flexi Leave Days">
              {data.flexiLeaveDays}
            </Descriptions.Item>
            <Descriptions.Item label="Weekend Days">
              {formatWeekendDays(data.weekendDays)}
            </Descriptions.Item>
            <Descriptions.Item label="Late Deduction?">
              {data.enableLateDeduction ? "Yes" : "No"}
            </Descriptions.Item>
            {data.enableLateDeduction && (
              <>
                <Descriptions.Item label="Late Deduction Type">
                  {data.lateDeductionType === "half_day" ? "Half Day" : data.lateDeductionType === "full_day" ? "Full Day" : data.lateDeductionType}
                </Descriptions.Item>
                <Descriptions.Item label="Late Deduction After">
                  {data.lateDeductionAfterMinutes} Minutes
                </Descriptions.Item>
                <Descriptions.Item label="Late Deduction From">
                  {data.lateDeductionLeaveType === "casual_leave"
                    ? isIndia
                      ? "Casual Leave"
                      : "Annual Leave"
                    : data.lateDeductionLeaveType === "sick_leave"
                      ? "Sick Leave"
                      : data.lateDeductionLeaveType === "emergency_leave"
                        ? "Emergency Leave (Unpaid)"
                        : data.lateDeductionLeaveType}
                </Descriptions.Item>
              </>
            )}
            <Descriptions.Item label="Unmarked Attendance Deduction?">
              {data.enableUnmarkedAttendanceDeduction ? "Yes" : "No"}
            </Descriptions.Item>
            {data.enableUnmarkedAttendanceDeduction && (
              <Descriptions.Item label="Unmarked Attendance Deduction From">
                {data.unmarkedAttendanceDeductionType === "casual_leave"
                  ? isIndia
                    ? "Casual Leave"
                    : "Annual Leave"
                  : data.unmarkedAttendanceDeductionType === "emergency_leave"
                    ? "Emergency Leave (Unpaid)"
                    : data.unmarkedAttendanceDeductionType}
              </Descriptions.Item>
            )}
            {data.enableUnmarkedAttendanceDeduction && (
              <Descriptions.Item label="Notify Before Deducting (days)">
                {Number(data.unmarkedAttendanceToleranceDays) > 0
                  ? `${data.unmarkedAttendanceToleranceDays} days`
                  : "Immediate (no notice)"}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Backdated Leave Allowed (Casual / Annual / Flexi)">
              {data.leaveBackdateDays ?? 7} day(s)
            </Descriptions.Item>
            <Descriptions.Item label="Backdated Sick Leave Allowed">
              {data.sickLeaveBackdateDays ?? 7} day(s)
            </Descriptions.Item>
            <Descriptions.Item label="Reset Accrued Leave?">
              {data.shouldResetAccruedLeave ? "Yes" : "No"}
            </Descriptions.Item>
            <Descriptions.Item label="Carry Forward">
              {data.leavesCarryForward}
            </Descriptions.Item>
            <Descriptions.Item label="Work Route Auto Checkout Duration (min)">
              {data.autoCheckoutDurationMinutes}
            </Descriptions.Item>
          </Descriptions>
        </Panel>

        <Panel
          key="bill"
          header={panelHeader("Company Bill Details", () => handleEdit("bill"))}
          data-onboard="bill"
        >
          <Descriptions {...descProps}>
            <Descriptions.Item label="Return Policy">
              {data.branchReturnPolicy}
            </Descriptions.Item>
            <Descriptions.Item label="Invoice Print Type">
              {data.invoicePrintType}
            </Descriptions.Item>
            <Descriptions.Item label="Is Warehouse">
              {data.isWarehouse ? "Yes" : "No"}
            </Descriptions.Item>
            <Descriptions.Item label={billType}>
              {data.isCalType === "on_total"
                ? "On Bill Total"
                : "On Individual Item"}
            </Descriptions.Item>
            <Descriptions.Item label="Is Auto Challan">
              {data.isAutoChallan ? "Yes" : "No"}
            </Descriptions.Item>
            <Descriptions.Item label="Bill Wise Collection">
              {data.isVoucherReceipt ? "Yes" : "No"}
            </Descriptions.Item>
            <Descriptions.Item label="Allow Edit of Product Rate in Sales">
              {data.allowProductRateChange ? "Yes" : "No"}
            </Descriptions.Item>
            <Descriptions.Item label="Cross-Branch Item Sync">
              {data.enableCrossBranchItemSync ? "Yes" : "No"}
            </Descriptions.Item>
            <Descriptions.Item label="Minus Stock Sales">
              {data.isMinusStockSale ? "Yes" : "No"}
            </Descriptions.Item>
            <Descriptions.Item label="Serial on Item">
              {data.isSerial ? "Yes" : "No"}
            </Descriptions.Item>
            <Descriptions.Item label="Batch Number">
              {data.enableBatchNumber ? "Enabled" : "Disabled"}
            </Descriptions.Item>
            <Descriptions.Item label="Discount on Avg Price">
              {data.applyDiscountToAvgPrice ? "Yes" : "No"}
            </Descriptions.Item>

            {/* Terms & Conditions fields */}
            <Descriptions.Item label="Default Quotation Terms">
              {data.quotationTerms || "N/A"}
            </Descriptions.Item>

            <Descriptions.Item label="Default Quotation Payment Terms">
              {data.quotationPaymentTerms || "N/A"}
            </Descriptions.Item>

            <Descriptions.Item label="Default PO Payment Terms">
              {data.purchaseOrderPaymentTerms || "N/A"}
            </Descriptions.Item>

            {/* Company Stamp */}
            <Descriptions.Item label="Company Stamp">
              {data.branchStamp ? (
                <img
                  src={data.branchStamp}
                  alt="Company Stamp"
                  style={{
                    maxHeight: "60px",
                    maxWidth: "120px",
                    objectFit: "contain",
                  }}
                />
              ) : (
                "N/A"
              )}
            </Descriptions.Item>

            {/* Company Signature */}
            <Descriptions.Item label="Company Signature">
              {data.branchSignature ? (
                <img
                  src={data.branchSignature}
                  alt="Company Signature"
                  style={{
                    maxHeight: "60px",
                    maxWidth: "120px",
                    objectFit: "contain",
                  }}
                />
              ) : (
                "N/A"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Thermal Print">
              {data.thermal_print === true ? "Yes" : "No"}
            </Descriptions.Item>
          </Descriptions>
        </Panel>

        <Panel
          key="count"
          header={panelHeader("Company Account Count")}
          data-onboard="count"
        >
          <Descriptions {...descProps}>
            <Descriptions.Item label="Branches">
              {data.usedBranchCount}/{data.totalBranchCount}
            </Descriptions.Item>
            <Descriptions.Item label="Users">
              {data.usedUserCount}/{data.totalUserCount}
            </Descriptions.Item>
            {data.totalBasicCount > 0 && (
              <Descriptions.Item label="Basic Users">
                {data.usedBasicCount}/{data.totalBasicCount}
              </Descriptions.Item>
            )}
            {data.totalCoreCount > 0 && (
              <Descriptions.Item label="Core Users">
                {data.usedCoreCount}/{data.totalCoreCount}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Panel>
      </Collapse>

      {/* onboard component start */}
      {showWizard && (
        <OnboardingWizard
          key="onboarding-wizard"
          visible={showWizard}
          onClose={() => {
            setShowWizard(false);
            removeOnboardingParam();
            setOnboardingStep(0);
            sessionStorage.setItem("onboarding_cta_closed", "true");
            sessionStorage.removeItem("onboarding_started");
          }}
          triggerEdit={handleEdit}
          setCurrentStepKey={setCurrentStepKey}
          step={onboardingStep}
          setStep={setOnboardingStep}
        />
      )}
      {/* onboard component end */}

      {/* Modal */}
      <Modal
        title={`Edit ${openModal?.toUpperCase() || ""}`}
        open={!!openModal}
        onCancel={() => setOpenModal(null)}
        onOk={handleSave}
        data-testid="modal-edit-company"
        confirmLoading={saving}
        width={700}
        destroyOnHidden
        okButtonProps={{
          className: openModal === currentStepKey ? "pulse-btn" : "",
        }}
      >
        <Form form={form} layout="vertical">
          {/* Company */}
          {openModal === "company" && (
            <>
              <Tooltip
                title={
                  <div style={{ padding: "4px" }}>
                    <span>Please review and update key settings like:</span>
                    <ul style={{ margin: "8px 0 0 0", paddingLeft: "18px" }}>
                      <li>Branch Logo</li>
                      <li>Company Name</li>
                      <li>Legal Name</li>
                      <li>EIN/SSN</li>
                    </ul>
                  </div>
                }
              >
                <InfoCircleOutlined
                  style={{ cursor: "pointer", color: "rgba(0, 0, 0, 0.45)" }}
                />
              </Tooltip>

              <Form.Item label="Branch Logo">
                <Upload
                  beforeUpload={async (file) => {
                    try {
                      await validateLogo(file);
                      return false; // prevent auto upload
                    } catch (err) {
                      message.error(err);
                      return Upload.LIST_IGNORE;
                    }
                  }}
                  accept="image/png,image/jpeg"
                  listType="picture-card"
                  maxCount={1}
                  fileList={logoFileList}
                  onChange={({ fileList }) => setLogoFileList(fileList)}
                  onPreview={handlePreview}
                  showUploadList={{
                    showPreviewIcon: true,
                    showRemoveIcon: true,
                    showDownloadIcon: false,
                  }}
                >
                  {logoFileList.length >= 1 ? null : "+ Upload"}
                </Upload>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Only PNG/JPG · Max 600×600px · Max 2MB
                </Text>
              </Form.Item>
              <Form.Item
                name="companyName"
                label="Company Name"
                rules={[{ required: true }]}
              >
                <Input data-testid="input-companyName" />
              </Form.Item>
              <Form.Item name="legalName" label="Legal Name">
                <Input data-testid="input-legalName" />
              </Form.Item>
              <Form.Item name="einSsn" label="EIN/SSN">
                <Input data-testid="input-einSsn" />
              </Form.Item>
              <Form.Item
                name="invoiceStartNumber"
                label="Invoice Start Number"
              >
                <AutoComplete
                  options={[{ value: "1" }, { value: "100" }]}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </>
          )}
          {/* Company Type */}
          {openModal === "companyType" && (
            <>
              <Form.Item name="taxForm" label="Tax Form">
                <Input data-testid="input-taxForm" />
              </Form.Item>
              {/* <Form.Item name="subscriptionType" label="Subscription Type"><Input /></Form.Item>
              <Form.Item name="subscriptionExpiry" label="Subscription Expiry"><DatePicker style={{ width: "100%" }} /></Form.Item>
              <Form.Item name="companyType" label="Company Type"><Input /></Form.Item> */}
            </>
          )}
          {/* Info */}
          {openModal === "info" && (
            <>
              <Form.Item name="companyEmail" label="Company Email">
                <Input data-testid="input-companyEmail" />
              </Form.Item>
              <Form.Item
                name="phoneNo"
                label="Phone"
                validateTrigger={["onChange", "onBlur"]}
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const normalized = value.startsWith("+") ? value : `+${value}`;
                      const phoneNumber = parsePhoneNumberFromString(normalized);

                      if (!phoneNumber || !phoneNumber.isValid()) {
                        return Promise.reject(new Error("Invalid phone number"));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <PhoneInput
                  country={defaultCountry}
                  data-testid="input-phoneNo"
                  inputStyle={{
                    width: "100%",
                  }}
                  placeholder="Contact no"
                // value and onChange are now handled automatically by Form.Item using "phoneNo"
                />
              </Form.Item>

              <Form.Item name="webSite" label="Website">
                <Input data-testid="input-website" />
              </Form.Item>
              <Form.Item name="timeZone" label="Time Zone">
                {/* <Input data-testid="input-timeZone" /> */}
                <TimeZoneSelect data-testid="input-timeZone" />
              </Form.Item>
            </>
          )}
          {/* Address */}
          {openModal === "address" && (
            <>
              <Form.Item name="streetAddress" label="Street Address">
                <Input data-testid="input-streetAddress" />
              </Form.Item>
              {/* <Form.Item name="country" label="Country"><Input /></Form.Item> */}
              <Form.Item name="poBox" label="PO Box">
                <InputNumber
                  data-testid="input-poBox"
                  style={{ width: "100%" }}
                  onWheel={(e) => e.target.blur()}
                />
              </Form.Item>
              <Form.Item name="countryCode" label="Country Code">
                <Input data-testid="input-countryCode" />
              </Form.Item>
            </>
          )}
          {/* Finance */}
          {openModal === "finance" && (
            <>
              {/* <Form.Item name="currency" label="Currency">
      <Input />
    </Form.Item> */}
              {/* Customer Code custom format START */}
              <Form.Item
                name="customerCodePrefix"
                label="Customer Code Prefix"
                rules={[
                  {
                    pattern: /^[A-Za-z]{2,10}$/,
                    message: "Only letters allowed (2–10 characters)",
                  },
                ]}
              >
                <Input
                  placeholder="CMR"
                  maxLength={10}
                  onChange={(e) => {
                    const val = e.target.value.toUpperCase();
                    form.setFieldsValue({ customerCodePrefix: val });
                  }}
                />
              </Form.Item>

              <Form.Item shouldUpdate>
                {() => {
                  const prefix =
                    form.getFieldValue("customerCodePrefix") || "CMR";
                  return <Text type="secondary">Preview: {prefix}001</Text>;
                }}
              </Form.Item>
              {/* Customer Code custom format END */}

              {/* <Form.Item name="vatPercent" label="VAT %">
                <InputNumber
                  data-testid="input-vatPercent"
                  min={0}
                  max={100}
                  style={{ width: "100%" }}
                  onWheel={(e) => e.target.blur()}
                />
              </Form.Item> */}
              {showTax && (
                <Form.Item name="vatLicense" label={taxLabel}>
                  <Input data-testid="input-vatLicense" />
                </Form.Item>
              )}
              <Form.Item name="tradeLicenseNo" label="Trade License No">
                <Input data-testid="input-tradeLicenseNo" />
              </Form.Item>
              <Form.Item name="tradeLicenseExpiry" label="Trade License Expiry">
                <DatePicker
                  data-testid="input-tradeLicenseExpiry"
                  style={{ width: "100%" }}
                />
              </Form.Item>

              <Divider style={{ margin: "16px 0" }} />
              <Text strong>Invoice / Print Layout</Text>
              <Form.Item
                name="printTemplate"
                label="Print Layout"
                tooltip="Design A is the new voucher PDF/print layout (navy header band, bank details, tax summary). Legacy keeps the previous layout — switch back here any time, nothing is deleted."
                style={{ marginTop: 12 }}
              >
                <Select data-testid="select-printTemplate">
                  <Option value="design_a">Design A (new)</Option>
                  <Option value="legacy">Legacy (previous layout)</Option>
                </Select>
              </Form.Item>
              <Form.Item label="Bank Account">
                <Text type="secondary">
                  Mark an account as the primary bank account in{" "}
                  <Typography.Link onClick={() => navigate("/accounts/account-entry")}>
                    Chart of Accounts
                  </Typography.Link>{" "}
                  — Design A documents show its bank details automatically.
                </Text>
              </Form.Item>
              <Form.Item name="invoiceDefaultTerms" label="Default Terms & Notes">
                <Input.TextArea
                  data-testid="input-invoiceDefaultTerms"
                  rows={3}
                  placeholder="Printed on the Design A invoice footer if filled in"
                />
              </Form.Item>

              {/* ✅ Conditional fields */}
              {(authInfo?.company_type === "Drug Store" ||
                authInfo?.company_type === "Pharmacy" ||
                authInfo?.subscription === "SuperERP") && (
                  <>
                    <Form.Item name="dhaMohNo" label="DHA/MOH No">
                      <Input />
                    </Form.Item>
                    <Form.Item name="dhaMohExpiry" label="DHA/MOH Expiry">
                      <DatePicker style={{ width: "100%" }} />
                    </Form.Item>
                    <Form.Item name="glnNo" label="GLN No">
                      <Input />
                    </Form.Item>
                    <Form.Item name="glnExpiry" label="GLN Expiry">
                      <DatePicker style={{ width: "100%" }} />
                    </Form.Item>
                  </>
                )}

              <Form.Item name="description" label="Description">
                <Input.TextArea rows={3} />
              </Form.Item>
              <Form.Item
                name="fyStartMonth"
                label="Financial Year Start Month"
                rules={[{ required: true, message: 'Please select FY start month' }]}
              >
                <Select placeholder="Select month">
                  {Array.from({ length: 12 }, (_, i) => (
                    <Option key={i + 1} value={i + 1}>
                      {dayjs().month(i).format('MMMM')}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {/* VAT Stagger Month */}

              <Form.Item shouldUpdate>
                {() => {
                  const month = form.getFieldValue('fyStartMonth') || 1;
                  const startMonth = dayjs().month(month - 1).format('MMMM');
                  const endMonth = dayjs().month(month - 2 < 0 ? 11 : month - 2).format('MMMM');
                  return (
                    <>
                      <Text type="secondary">
                        Financial year runs from {startMonth} to {endMonth}
                      </Text>
                      <Text type="secondary">
                        Please Logout and Login again to see the changes in effect.
                      </Text>
                    </>
                  );
                }}
              </Form.Item>
              {isUAE && (
                <>
                  <Form.Item
                    name="vatStaggerMonth"
                    label="VAT Stagger Month"
                    tooltip="First month of your VAT quarter as assigned by UAE FTA"
                  >
                    <Select placeholder="Select stagger start month">
                      <Option value={1}>January</Option>
                      <Option value={2}>February</Option>
                      <Option value={3}>March</Option>
                      <Option value={3}>April</Option>
                      <Option value={3}>May</Option>
                      <Option value={3}>June</Option>
                      <Option value={3}>July</Option>
                      <Option value={3}>August</Option>
                      <Option value={3}>September</Option>
                      <Option value={3}>October</Option>
                      <Option value={11}>November</Option>
                      <Option value={11}>December</Option>
                    </Select>
                  </Form.Item>
                  <Form.Item
                    name="vatDeadlineDay"
                    label="VAT Filing Deadline Day"
                    tooltip="Day of the month by which VAT must be filed after each quarter ends. Usually 28."
                  >
                    <InputNumber
                      min={1}
                      max={31}
                      style={{ width: "100%" }}
                      placeholder="e.g. 28"
                    />
                  </Form.Item>
                </>
              )}
              <Form.Item name="migrationDate" label="Migration Date">
                <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
              </Form.Item>
            </>
          )}

          {/* HR */}
          {openModal === "hr" && (
            <>
              <Form.Item name="annualLeaveType" label="Annual Leave Type">
                <Select data-testid="select-annualLeaveType">
                  <Option value="Calendar Days">Calendar Days</Option>
                  <Option value="Working Days">Working Days</Option>
                </Select>
              </Form.Item>
              <Form.Item
                name="annualLeaveDays"
                label={isIndia ? "Casual Leave Days" : "Annual Leave Days"}
              >
                <InputNumber
                  data-testid="input-annualLeaveDays"
                  style={{ width: "100%" }}
                  onWheel={(e) => e.target.blur()}
                  onFocus={() => {
                    form.setFieldValue("annualLeaveDays", "");
                  }}
                />
              </Form.Item>
              <Form.Item name="sickLeaveDays" label="Maximum Sick Leave Days">
                <InputNumber
                  style={{ width: "100%" }}
                  onWheel={(e) => e.target.blur()}
                  onFocus={() => {
                    if (form.getFieldValue("sickLeaveDays") === 0) {
                      form.setFieldValue("sickLeaveDays", "");
                    }
                  }}
                />
              </Form.Item>
              <Form.Item
                name="flexiLeaveDays"
                label="Flexi Leave Days"
                extra="Granted after probation; does not carry forward."
              >
                <InputNumber
                  style={{ width: "100%" }}
                  onWheel={(e) => e.target.blur()}
                  onFocus={() => {
                    if (form.getFieldValue("flexiLeaveDays") === 0) {
                      form.setFieldValue("flexiLeaveDays", "");
                    }
                  }}
                />
              </Form.Item>
              <Form.Item name="weekendDays" label="Weekend Days">
                <Select data-testid="select-weekendDays" mode="multiple">
                  {Object.entries(WEEK_DAYS).map(([val, label]) => (
                    <Option key={val} value={Number(val)}>
                      {label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Divider style={{ margin: "16px 0" }} />
              <div>
                <Form.Item
                  name="enableLateDeduction"
                  label="Enable Late Deduction"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
                <Form.Item
                  shouldUpdate={(prev, curr) =>
                    prev.enableLateDeduction !== curr.enableLateDeduction ||
                    prev.lateDeductionType !== curr.lateDeductionType
                  }
                  style={{ marginBottom: 0 }}
                >
                  {({ getFieldValue, setFieldValue }) =>
                    getFieldValue("enableLateDeduction") && (
                      <>
                        <Form.Item
                          name="lateDeductionType"
                          label="Deduction Type"
                        >
                          <Select
                            onChange={(value) => {
                              // Emergency Leave (Unpaid) has no half-day equivalent
                              // in the schema — switching to Half Day while it's
                              // selected would silently save an unsupported
                              // combination, so fall back to the pooled leave.
                              if (
                                value === "half_day" &&
                                getFieldValue("lateDeductionLeaveType") === "emergency_leave"
                              ) {
                                setFieldValue("lateDeductionLeaveType", "casual_leave");
                              }
                            }}
                            options={[
                              {
                                label: "Half Day",
                                value: "half_day",
                              },
                              {
                                label: "Full Day",
                                value: "full_day",
                              },
                            ]}
                          />
                        </Form.Item>

                        <Form.Item
                          name="lateDeductionAfterMinutes"
                          label="Late After Minutes"
                        >
                          <InputNumber min={1} style={{ width: "100%" }} />
                        </Form.Item>

                        <Form.Item
                          name="lateDeductionLeaveType"
                          label="Deduct From"
                          extra="Which leave balance the deduction is taken from."
                        >
                          <Select
                            options={[
                              {
                                label: isIndia ? "Casual Leave" : "Annual Leave",
                                value: "casual_leave",
                              },
                              {
                                label: "Sick Leave",
                                value: "sick_leave",
                              },
                              ...(getFieldValue("lateDeductionType") === "full_day"
                                ? [
                                    {
                                      label: "Emergency Leave (Unpaid)",
                                      value: "emergency_leave",
                                    },
                                  ]
                                : []),
                            ]}
                          />
                        </Form.Item>
                      </>
                    )
                  }
                </Form.Item>
              </div>
              <Divider style={{ margin: "16px 0" }} />
              <div>
                <Form.Item
                  name="enableUnmarkedAttendanceDeduction"
                  label="Unmarked Attendance Deduction"
                  valuePropName="checked"
                  tooltip="If an employee forgets to check in for a day and it's never regularized, automatically deduct a leave day instead of just marking it Absent."
                >
                  <Switch />
                </Form.Item>
                <Form.Item
                  shouldUpdate={(prev, curr) =>
                    prev.enableUnmarkedAttendanceDeduction !== curr.enableUnmarkedAttendanceDeduction
                  }
                  style={{ marginBottom: 0 }}
                >
                  {({ getFieldValue }) =>
                    getFieldValue("enableUnmarkedAttendanceDeduction") && (
                      <>
                        <Form.Item
                          name="unmarkedAttendanceDeductionType"
                          label="Deduct From"
                          extra="Which leave balance an unmarked (forgotten check-in) day deducts from, unless regularized later."
                          rules={[
                            {
                              required: true,
                              message: "Select which leave balance unmarked attendance should deduct from.",
                            },
                          ]}
                        >
                          <Select
                            placeholder="Select a leave balance"
                            options={[
                              {
                                label: isIndia ? "Casual Leave" : "Annual Leave",
                                value: "casual_leave",
                              },
                              {
                                label: "Emergency Leave (Unpaid)",
                                value: "emergency_leave",
                              },
                            ]}
                          />
                        </Form.Item>

                        <Form.Item
                          name="unmarkedAttendanceToleranceDays"
                          label="Notify Before Deducting (days)"
                          extra="Email the employee and give them this many days to regularize the day or apply leave before it's deducted. 0 = deduct immediately, no notice."
                          rules={[
                            {
                              required: true,
                              message: "Enter the number of notice days (0 for immediate deduction).",
                            },
                          ]}
                        >
                          <InputNumber min={0} placeholder="e.g. 3" style={{ width: "100%" }} />
                        </Form.Item>
                      </>
                    )
                  }
                </Form.Item>
              </div>
              <Divider style={{ margin: "16px 0" }} />
              <div>
                <Form.Item
                  name="leaveBackdateDays"
                  label="Backdated Leave Allowed (days)"
                  tooltip="How many days back an employee can apply Casual / Annual / Flexi / Short Leave. 0 = same day only. HR can still apply leave for older dates on the employee's behalf."
                >
                  <InputNumber min={0} max={365} style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item
                  name="sickLeaveBackdateDays"
                  label="Backdated Sick Leave Allowed (days)"
                  tooltip="How many days back an employee can apply Sick Leave (often longer, since a medical certificate may arrive later)."
                >
                  <InputNumber min={0} max={365} style={{ width: "100%" }} />
                </Form.Item>
              </div>
              <Divider style={{ margin: "16px 0" }} />
              <div>
                <Form.Item
                  name="shouldResetAccruedLeave"
                  label="Reset Accrued Leave"
                  valuePropName="checked"
                  tooltip="Unused Casual/Annual leave from the previous leave year (which runs from each employee's joining-date anniversary) always carries forward up to 'No. of Leaves Carry Forward', and the rest is reset. This switch currently does not change any leave balance or Final Settlement calculation."
                >
                  <Switch data-testid="switch-resetAccruedLeave" />
                </Form.Item>
                <Form.Item
                  name="leavesCarryForward"
                  label="No. of Leaves Carry Forward"
                  tooltip="Maximum number of unused Casual/Annual leave days from the previous leave year that carry into the new one. Can be overridden per employee on the Employee page."
                >
                  <InputNumber style={{ width: "100%" }}
                    onWheel={(e) => e.target.blur()}
                    onFocus={() => {
                      if (form.getFieldValue("leavesCarryForward") === 0) {
                        form.setFieldValue("leavesCarryForward", "");
                      }
                    }}
                  />
                </Form.Item>
              </div>
              <Divider style={{ margin: "16px 0" }} />
              <Form.Item
                label={
                  <span>
                    Work Route Auto Checkout Duration
                    <Tooltip title="Automatically check out an employee after this duration from their check-in time.">
                      <QuestionCircleOutlined style={{ marginLeft: 5 }} />
                    </Tooltip>
                  </span>
                }
                name="autoCheckoutDurationMinutes"
                extra="Leave this empty to disable automatic checkout."
              >
                <InputNumber
                  min={1}
                  step={1}
                  precision={0}
                  style={{ width: "100%" }}
                  placeholder="Enter duration in minutes"
                  addonAfter="minutes"
                />
              </Form.Item>
            </>
          )}
          {/* Bill */}
          {openModal === "bill" && (
            <>
              <Form.Item name="branchReturnPolicy" label="Return Policy (days)">
                <InputNumber
                  data-testid="input-returnPolicy"
                  min={0}
                  style={{ width: "100%" }}
                  onWheel={(e) => e.target.blur()}
                />
              </Form.Item>

              {/* <Form.Item
                name="branchReplacePolicy"
                label="Replace Policy (days)"
              >
                <InputNumber
                  data-testid="input-replacePolicy"
                  min={0}
                  style={{ width: "100%" }}
                  onWheel={(e) => e.target.blur()}
                />
              </Form.Item> */}

              <Form.Item name="invoicePrintType" label="Invoice Print Type">
                <Radio.Group data-testid="radio-invoicePrintType">
                  <Radio value="a4" data-testid="radio-invoice-a4">
                    A4
                  </Radio>
                  <Radio value="1/2a4" data-testid="radio-invoice-half-a4">
                    1/2 of A4
                  </Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item name="isCalType" label="Vat & Discount on Bill Type">
                <Radio.Group data-testid="radio-calType">
                  <Radio value="on_total" data-testid="radio-cal-total">
                    On Bill Total
                  </Radio>
                  <Radio value="individual" data-testid="radio-cal-individual">
                    On Individual Item
                  </Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                name="isWarehouse"
                label="Is Warehouse"
                valuePropName="checked"
              >
                <Switch data-testid="switch-isWarehouse" />
              </Form.Item>
              <Form.Item
                name="thermalPrint"
                label="Enable Thermal Print (112mm)"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              <Form.Item
                name="isAutoChallan"
                label="Is Auto Challan"
                valuePropName="checked"
              >
                <Switch data-testid="switch-isAutoChallan" />
              </Form.Item>

              <Form.Item
                name="isVoucherReceipt"
                label="Bill Wise Collection"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="allowProductRateChange"
                label="Allow Edit of Product Rate"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="enableCrossBranchItemSync"
                label="Enable Cross-Branch Item Sync"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="isMinusStockSale"
                label="Minus Stock Sales"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="isSerial"
                label="Serial on Item"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="enableBatchNumber"
                label={
                  <span>
                    Enable Batch Number{" "}
                    <Tag color="warning" style={{ marginLeft: 4 }}>
                      Requires re-login
                    </Tag>
                  </span>
                }
                valuePropName="checked"
                tooltip="When off, Batch Number is hidden from Sales, Purchase, and Stock Adjustment throughout the app. Changing this setting requires logging out and back in to take effect."
              >
                <Switch disabled={authInfo?.is_tatmeen_enabled === "yes"} />
              </Form.Item>
              {authInfo?.is_tatmeen_enabled === "yes" ? (
                <Alert
                  type="warning"
                  showIcon
                  message="Batch Number can't be disabled while Tatmeen compliance is enabled."
                  style={{ marginBottom: 8 }}
                />
              ) : (
                <Alert
                  type="warning"
                  showIcon
                  message="Please Logout and Login again to see the changes in effect."
                  style={{ marginBottom: 8 }}
                />
              )}

              <Form.Item
                name="applyDiscountToAvgPrice"
                label="Discount on Avg Price"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                label={
                  <Space>
                    Default Quotation Terms
                    <Tooltip
                      title="Default quotation terms used for auto-filling quotation entry forms.
                                      Each line will appear as a bullet point in quotation print/PDF."
                    >
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="quotationTerms"
              >
                <Input.TextArea
                  placeholder={`Enter one term per line
        Example:
        18% TAX extra
        Offer valid for 30 days
        Delivery within 10 days`}
                  autoSize={{ minRows: 4, maxRows: 12 }}
                  style={{
                    lineHeight: 1.8,
                    overflowY: "auto",
                    paddingRight: 12,
                    backgroundImage:
                      "linear-gradient(to bottom, #f1f1f1 1px, transparent 1px)",
                    backgroundSize: "100% 32px",
                  }}
                  onBlur={(e) => {
                    form.setFieldsValue({
                      quotationTerms: formatBulletText(e.target.value),
                    });
                  }}
                />
              </Form.Item>

              <Form.Item
                label={
                  <Space>
                    Default Quotation Payment Terms
                    <Tooltip
                      title="Quotation payment terms used for auto-filling quotation entry forms.
                                     Each line will appear as a bullet point in quotation print/PDF."
                    >
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="quotationPaymentTerms"
              >
                <Input.TextArea
                  placeholder={`Enter one term per line
        Example:
        50% Advance
        Remaining After Delivery`}
                  autoSize={{ minRows: 4, maxRows: 12 }}
                  style={{
                    lineHeight: 1.8,
                    overflowY: "auto",
                    paddingRight: 12,
                    backgroundImage:
                      "linear-gradient(to bottom, #f1f1f1 1px, transparent 1px)",
                    backgroundSize: "100% 32px",
                  }}
                  onBlur={(e) => {
                    form.setFieldsValue({
                      quotationPaymentTerms: formatBulletText(e.target.value),
                    });
                  }}
                />
              </Form.Item>

              <Form.Item
                label={
                  <Space>
                    Default PO Payment Terms
                    <Tooltip
                      title="Default PO payment  terms used for auto-filling PO entry forms.
                                  Each line will appear as a bullet point in quotation print/PDF."
                    >
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                }
                name="purchaseOrderPaymentTerms"
              >
                <Input.TextArea
                  autoSize={{ minRows: 4, maxRows: 12 }}
                  placeholder={`Enter one term per line
        Example:
        18% TAX extra
        Offer valid for 30 days
        Delivery within 10 days`}
                  style={{
                    lineHeight: 1.8,
                    overflowY: "auto",
                    paddingRight: 12,
                    backgroundImage:
                      "linear-gradient(to bottom, #f1f1f1 1px, transparent 1px)",
                    backgroundSize: "100% 32px",
                  }}
                  onBlur={(e) => {
                    form.setFieldsValue({
                      purchaseOrderPaymentTerms: formatBulletText(
                        e.target.value,
                      ),
                    });
                  }}
                />
              </Form.Item>

              <Form.Item label="Branch Logo">
                <Upload
                  beforeUpload={async (file) => {
                    try {
                      await validateLogo(file);
                      return false; // prevent auto upload
                    } catch (err) {
                      message.error(err);
                      return Upload.LIST_IGNORE;
                    }
                  }}
                  accept="image/png,image/jpeg"
                  listType="picture-card"
                  maxCount={1}
                  fileList={logoFileList}
                  onChange={({ fileList }) => setLogoFileList(fileList)}
                  onPreview={handlePreview}
                  showUploadList={{
                    showPreviewIcon: true,
                    showRemoveIcon: true,
                    showDownloadIcon: false,
                  }}
                >
                  {logoFileList.length >= 1 ? null : "+ Upload"}
                </Upload>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Only PNG/JPG · Max 300×300px · Max 2MB
                </Text>
              </Form.Item>

              <Form.Item label="Branch Stamp">
                <Upload
                  beforeUpload={async (file) => {
                    try {
                      await validateStamp(file);
                      return false; // prevent auto upload
                    } catch (err) {
                      message.error(err);
                      return Upload.LIST_IGNORE;
                    }
                  }}
                  accept="image/png,image/jpeg"
                  listType="picture-card"
                  maxCount={1}
                  fileList={stampFileList}
                  onChange={({ fileList }) => setStampFileList(fileList)}
                  onPreview={handlePreview}
                  showUploadList={{
                    showPreviewIcon: true,
                    showRemoveIcon: true,
                    showDownloadIcon: false,
                  }}
                >
                  {stampFileList.length >= 1 ? null : "+ Upload"}
                </Upload>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Only PNG/JPG · Max 600×600px · Max 2MB
                </Text>
              </Form.Item>

              <Form.Item label="Branch Signature">
                <Upload
                  beforeUpload={async (file) => {
                    try {
                      await validateSignature(file);
                      return false; // prevent auto upload
                    } catch (err) {
                      message.error(err);
                      return Upload.LIST_IGNORE;
                    }
                  }}
                  accept="image/png,image/jpeg"
                  listType="picture-card"
                  maxCount={1}
                  fileList={signatureFileList}
                  onPreview={handlePreview}
                  showUploadList={{
                    showPreviewIcon: true,
                    showRemoveIcon: true,
                    showDownloadIcon: false,
                  }}
                  onChange={({ fileList }) => setSignatureFileList(fileList)}
                >
                  {signatureFileList.length >= 1 ? null : "+ Upload"}
                </Upload>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Only PNG/JPG · Max 800×400px · Max 2MB
                </Text>
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </Card>
  );
};

export default InstitutionProfile;
